/*
 * Per-race required public signup fields (LiveRace).
 *
 * Firstname, lastname, cellphone and email are always required: cellphone is
 * the login, firstname/lastname are the person, email gets the receipt. The
 * rest is decided by the organizer through the race's signupRequire* toggles.
 * The same table lives in backend/lib/race-class-signup.js, which enforces it
 * on signup and on owner edits.
 *
 * Nothing here mutates the shared field definitions from fields.get(): the
 * functions return copies and memoize them per sections instance + race flags,
 * so a render loop gets stable section instances back.
 */

export const SIGNUP_ALWAYS_REQUIRED_FIELDS = ['firstname', 'lastname', 'cellphone', 'email'];

export const SIGNUP_REQUIRED_FIELD_OPTIONS = [
    { raceField: 'signupRequireSportsClub', fields: ['sportsClub'] },
    { raceField: 'signupRequireKennel', fields: ['kennel'] },
    { raceField: 'signupRequireBirth', fields: ['birth'] },
    { raceField: 'signupRequireCountry', fields: ['country'] },
    { raceField: 'signupRequireAddress', fields: ['address', 'postalcode', 'postalplace'] },
    { raceField: 'signupRequireLicenseNumber', fields: ['licenseNumber'] },
    { raceField: 'signupRequireQualifying', fields: ['qualifying'] },
];

const CONFIGURABLE_FIELD_NAMES = new Set(
    SIGNUP_REQUIRED_FIELD_OPTIONS.reduce((names, option) => names.concat(option.fields), []),
);

const isEnabled = value => value === true || value === 1 || value === '1';

export function getRequiredSignupFieldNames(race) {
    const flags = race || {};
    const required = [...SIGNUP_ALWAYS_REQUIRED_FIELDS];
    SIGNUP_REQUIRED_FIELD_OPTIONS.forEach(option => {
        if (isEnabled(flags[option.raceField])) {
            required.push(...option.fields);
        }
    });
    return required;
}

const getRaceFlagsKey = race => {
    const flags = race || {};
    return SIGNUP_REQUIRED_FIELD_OPTIONS.map(option => (isEnabled(flags[option.raceField]) ? '1' : '0')).join('');
};

// sections instance -> Map(flagsKey -> applied sections)
const appliedSectionsCache = new WeakMap();

export function applyRaceRequiredFields(sections, race) {
    if (!Array.isArray(sections) || sections.length === 0) {
        return [];
    }
    const flagsKey = getRaceFlagsKey(race);
    let byFlags = appliedSectionsCache.get(sections);
    if (!byFlags) {
        byFlags = new Map();
        appliedSectionsCache.set(sections, byFlags);
    }
    if (byFlags.has(flagsKey)) {
        return byFlags.get(flagsKey);
    }
    const required = new Set(getRequiredSignupFieldNames(race));
    const applied = sections.map(section => {
        if (!section || !Array.isArray(section.fields)) {
            return section;
        }
        return {
            ...section,
            fields: section.fields.map(field => {
                const isManaged = SIGNUP_ALWAYS_REQUIRED_FIELDS.includes(field.name)
                    || CONFIGURABLE_FIELD_NAMES.has(field.name);
                if (!isManaged) {
                    return field;
                }
                return { ...field, isRequired: required.has(field.name) };
            }),
        };
    });
    byFlags.set(flagsKey, applied);
    return applied;
}

const isEmptyValue = value => {
    if (value === undefined || value === null) {
        return true;
    }
    if (typeof value === 'string') {
        return value.trim() === '';
    }
    if (value instanceof Date) {
        return Number.isNaN(value.getTime());
    }
    if (typeof value === 'number') {
        return Number.isNaN(value);
    }
    return false;
};

// Returns the field definitions (with title etc.) of required fields that are
// empty on the object, in section order. Whitespace-only strings count as
// empty, matching the backend check.
export function getMissingRequiredFields(obj, sections) {
    if (!Array.isArray(sections)) {
        return [];
    }
    const values = obj || {};
    const missing = [];
    sections.forEach(section => {
        (section?.fields || []).forEach(field => {
            if (field.isRequired && isEmptyValue(values[field.name])) {
                missing.push(field);
            }
        });
    });
    return missing;
}
